import React, { useEffect, useState } from 'react';
import { send_request } from '../../utils/Request';
import { Box, CircularProgress } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { makeStyles } from '@mui/styles';
import Badge from '@mui/material/Badge';
import useTaskInboxStore from '../pages/useTaskInboxStore';

const useStyles = makeStyles(theme => ({
  badge: {
    fontSize: 10,
    right: 3,
    top: 1,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
  },
}));

// Cache the agent count so we don't re-fetch on every sidebar render
let cachedAgentCount = null;
let agentCountPromise = null;

function getAgentActiveCount() {
  // If the agent store already has data, use it directly (no API call)
  const storeTotal = useTaskInboxStore.getState().taskInboxTotal;
  if (storeTotal > 0) {
    cachedAgentCount = storeTotal;
    return Promise.resolve(storeTotal);
  }

  // Return cached value if available
  if (cachedAgentCount !== null) {
    return Promise.resolve(cachedAgentCount);
  }

  // Deduplicate in-flight requests
  if (agentCountPromise) return agentCountPromise;

  agentCountPromise = send_request('project-service/agent/forms', '', { limit: 1, status: 'ACTIVE' }, 'GET')
    .then(res => {
      const count = Number(res?.data?.total) || 0;
      cachedAgentCount = count;
      agentCountPromise = null;
      return count;
    })
    .catch(() => {
      agentCountPromise = null;
      return cachedAgentCount || 0;
    });

  return agentCountPromise;
}

function TaskNotificationsBadge() {
  const classes = useStyles();

  const [incompleteTaskCount, setIncompleteTaskCount] = useState(cachedAgentCount !== null ? cachedAgentCount : null);
  const [loading, setLoading] = useState(cachedAgentCount === null);

  // Subscribe to store total so badge updates when inbox loads
  const storeTotal = useTaskInboxStore((s) => s.taskInboxTotal);

  useEffect(() => {
    if (storeTotal > 0 && cachedAgentCount !== storeTotal) {
      cachedAgentCount = storeTotal;
    }
  }, [storeTotal]);

  useEffect(() => {
    const legacyPromise = send_request('task/task/get_incomplete_task_count')
      .then(res => Number(res?.data) || 0)
      .catch(() => 0);

    const agentPromise = getAgentActiveCount();

    Promise.all([legacyPromise, agentPromise])
      .then(([legacyCount, agentCount]) => {
        setIncompleteTaskCount(legacyCount + agentCount);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  // Update badge when store total changes (e.g., after inbox loads)
  useEffect(() => {
    if (storeTotal > 0 && !loading) {
      send_request('task/task/get_incomplete_task_count')
        .then(res => {
          const legacyCount = Number(res?.data) || 0;
          setIncompleteTaskCount(legacyCount + storeTotal);
        })
        .catch(() => {});
    }
  }, [storeTotal]);

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <Badge
        size="sm"
        badgeContent={loading ? 0 : incompleteTaskCount}
        invisible={loading}
        color="secondary"
        classes={{ badge: classes.badge }}
      >
        <InboxOutlinedIcon />
      </Badge>
      {loading && (
        <CircularProgress
          size={20}
          thickness={2}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginTop: '-10px',
            marginLeft: '-10px',
            color: 'text.disabled',
          }}
        />
      )}
    </Box>
  );
}

export default TaskNotificationsBadge;
